import { axiosClient } from '@/shared';
import {
  DtoProjectStatus,
  DtoProjectType,
  DtoProjectErrorType,
} from '@/shared/constants';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useQuery } from '@tanstack/react-query';
import { z, ZodError } from 'zod';

// v2.0 진행률 스키마
const statusProgressSchema = z.object({
  current: z.number(),
  total: z.number(),
  label: z.string(),
  statusKey: z.string(),
});

// 스페이스 홈 응답 데이터 스키마 (v2.0)
export const spaceHomeSchema = z.object({
  spaceId: z.number(),
  rootFolderId: z.number(),
  recentProjectList: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      projectType: z.nativeEnum(DtoProjectType),
      status: z.nativeEnum(DtoProjectStatus),
      errorCode: z.nativeEnum(DtoProjectErrorType).nullable(),
      indexLevel: z.number().nullable().default(null),
      createdAt: z.string(),
      updatedAt: z.string(),
      thumbnailUrls: z.array(z.string()),
      childCount: z.number(),
      totalDuration: z.number(), // 총 영상 길이 (밀리초)
      statusProgress: statusProgressSchema.nullable(),
      isRetryable: z.boolean().default(false),
      failedStep: z.string().nullable().default(null),
      retryCount: z.number().default(0),
      // info-video(AGENT_ITS) 프로젝트만 recipe(source-first/script-first)를 실어 준다.
      // 그 외 프로젝트 타입은 null. 재진입 라우팅(ProjectContentCard)이 이 값으로 분기한다.
      recipeKey: z.string().nullable().optional(),
    }),
  ),
});

export type SpaceHomeResType = z.infer<typeof spaceHomeSchema>;

type SpaceHomeResponse = ApiResponse<SpaceHomeResType>;

export interface SpaceHomeParams {
  projectType?: DtoProjectType;
  indexLevel?: number;
  limit?: number;
}

type UseGetSpaceHomeApiOptions = CustomQueryOptions<SpaceHomeResponse> & {
  params?: SpaceHomeParams;
};

export const filterVisibleSpaceHomeProjects = (
  data: SpaceHomeResType,
): SpaceHomeResType => ({
  ...data,
  recentProjectList: data.recentProjectList.filter(
    (project) => project.projectType !== DtoProjectType.bulkShortform,
  ),
});

export const spaceHomeApi = async (
  params?: SpaceHomeParams,
): Promise<SpaceHomeResponse> => {
  try {
    const response = await axiosClient.get<SpaceHomeResponse>(
      '/v2.0/space/home',
      { params },
    );

    const parsedData = spaceHomeSchema.parse(response.data.data);

    return {
      ...response.data,
      data: filterVisibleSpaceHomeProjects(parsedData),
    };
  } catch (error) {
    if (error instanceof ZodError) {
      console.error('API 응답 유효성 검증 실패:', error.issues);
      return Promise.reject(
        new Error('서버 응답 데이터 형식이 올바르지 않습니다.'),
      );
    }

    return Promise.reject(error);
  }
};

export const QueryKeyUseSpaceHome = 'spaceHome' as const;

export const useGetSpaceHomeApi = (options?: UseGetSpaceHomeApiOptions) => {
  const { queryKey, params, ...rest } = options ?? {};

  return useQuery({
    queryKey: [QueryKeyUseSpaceHome, params ?? null, ...(queryKey ?? [])],
    queryFn: async () => await spaceHomeApi(params),
    ...rest,
  });
};
